import { DensityFunction, Identifier, Json } from "deepslate";

type QuantizedParam = { id: string, min: bigint, max: bigint }

function quantize(value: number): bigint {
    return BigInt(Math.trunc(Math.fround(Math.fround(value) * 10000)))
}

export class SpawnTarget{

    constructor(
        private points: QuantizedParam[][],
        private functions: Map<string, DensityFunction>,
    ){

    }

    public static fromJson(obj: unknown, resolve: (id: Identifier) => DensityFunction | undefined){
        const functions = new Map<string, DensityFunction>()
        const points = Json.readArray(obj, (e) => {
            const root = Json.readObject(e) ?? {}
            const point: QuantizedParam[] = []
            for (const [key, value] of Object.entries(root)) {
                const id = Identifier.parse(key)
                const fn = functions.get(id.toString()) ?? resolve(id)
                if (fn === undefined) continue
                functions.set(id.toString(), fn)
                const range = typeof value === "number" ? [value, value] : Json.readArray(value, e => Json.readNumber(e) ?? 0) ?? [0, 0]
                point.push({ id: id.toString(), min: quantize(range[0]), max: quantize(range[1]) })
            }
            return point
        }) ?? []
        return new SpawnTarget(points, functions)
    }

    public getSpawnPoint(): [number, number] {
        const self = this
        var result: [number, number] = [0, 0]
        var result_fitness = getFitness(0, 0)

        radialSearch(2048, 512)
        radialSearch(512, 32)

        return result

        function getFitness(x: number, z: number){
            const context = DensityFunction.context((x >> 2) << 2, 0, (z >> 2) << 2)
            const values = new Map<string, bigint>()
            for (const [id, fn] of self.functions) {
                values.set(id, quantize(fn.compute(context)))
            }
            var climateFitness: bigint | undefined = undefined
            for (const point of self.points) {
                var sum = 0n
                for (const { id, min, max } of point) {
                    const value = values.get(id)!
                    const distance = value > max ? value - max : min > value ? min - value : 0n
                    sum += distance * distance
                }
                if (climateFitness === undefined || sum < climateFitness) climateFitness = sum
            }
            return (climateFitness ?? 0n) * 2048n * 2048n + BigInt(x) * BigInt(x) + BigInt(z) * BigInt(z)
        }

        function radialSearch(maxRadius: number, radiusStep: number) {
            const [centerX, centerZ] = result
            var angle = 0
            var radius = Math.fround(radiusStep)

            while(radius <= maxRadius) {
                const x = centerX + Math.trunc(Math.sin(angle) * radius)
                const z = centerZ + Math.trunc(Math.cos(angle) * radius)
                const fitness = getFitness(x, z)
                if (fitness < result_fitness) {
                    result = [x, z];
                    result_fitness = fitness
                }

                angle = Math.fround(angle + Math.fround(radiusStep / radius))
                if (angle > Math.PI * 2) {
                    angle = 0
                    radius = Math.fround(radius + radiusStep)
                }
            }
        }
    }

    public static findSpawnInChunks(target: [number, number], isDryColumn: (x: number, z: number) => boolean, budgetMs: number): { pos: [number, number], exact: boolean } {
        const centerX = target[0] >> 4
        const centerZ = target[1] >> 4
        const deadline = performance.now() + budgetMs
        var dx = 0, dz = 0, stepX = 0, stepZ = -1
        for (var i = 0; i < 121; i++) {
            if (dx >= -5 && dx <= 5 && dz >= -5 && dz <= 5) {
                const chunkX = (centerX + dx) << 4
                const chunkZ = (centerZ + dz) << 4
                for (var x = chunkX; x < chunkX + 16; x++) {
                    for (var z = chunkZ; z < chunkZ + 16; z++) {
                        if (isDryColumn(x, z)) return { pos: [x, z], exact: true }
                        if (performance.now() > deadline) return { pos: [(centerX << 4) + 8, (centerZ << 4) + 8], exact: false }
                    }
                }
            }
            if (dx === dz || (dx < 0 && dx === -dz) || (dx > 0 && dx === 1 - dz)) {
                const t = stepX
                stepX = -stepZ
                stepZ = t
            }
            dx += stepX
            dz += stepZ
        }
        return { pos: [(centerX << 4) + 8, (centerZ << 4) + 8], exact: true }
    }
}
